/**
 * Which app-mode lists a set of roles still appears on.
 */

import type { AppMode } from '@/contexts/ModeContext';
import { roleMode } from '@/utils/role-label-utils';

/** Role id + code used to resolve list visibility by mode. */
export type RoleListModeRef = { id: string; code: string };

/** List modes where the selected roles still appear (hybrid / null → both). */
function collectRoleListModes(roleIds: string[], roles: RoleListModeRef[]): Set<AppMode> {
  const modes = new Set<AppMode>();
  for (const role of roles) {
    if (!roleIds.includes(role.id)) continue;
    const mode = roleMode(role.code);
    if (mode === 'remote') modes.add('remote');
    else if (mode === 'in_person') modes.add('in_person');
    else {
      modes.add('in_person');
      modes.add('remote');
    }
  }
  return modes;
}

/**
 * Cross-mode list when none of the selected roles belong to `currentMode`.
 * Null when the person stays visible on the current-mode list.
 */
export function getCrossModeList(
  roleIds: string[],
  roles: RoleListModeRef[],
  currentMode: AppMode | null,
): AppMode | null {
  if (!currentMode || roleIds.length === 0) return null;
  const modes = collectRoleListModes(roleIds, roles);
  if (modes.size === 0 || modes.has(currentMode)) return null;
  return modes.has('in_person') ? 'in_person' : 'remote';
}
